import { fmt, fmtDate, fmtQty, type PurchaseJournal } from '@k2pos/core';
import { useParams } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { useGoBack } from '@/hooks/useNav';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './PurchaseDetail.module.css';

/**
 * One delivery, in full.
 *
 * The delivery note, kept: what arrived, in the unit it arrived in, what each
 * unit cost and what the lorry-load came to. It is what a shopkeeper holds
 * against a supplier's invoice when the two disagree, so it shows what was
 * recorded that day and nothing recomputed since.
 */
export function PurchaseDetail() {
  const { id } = useParams<{ id: string }>();
  const goBack = useGoBack('/purchases');
  const catalog = usePos((s) => s.catalog);
  const retired = usePos((s) => s.retired);
  const purchase = usePos((s) =>
    s.journal.find((e): e is PurchaseJournal => e.kind === 'purchase' && e.id === id),
  );

  if (!purchase) {
    return (
      <>
        <AppBar title="ບໍ່ພົບລາຍການ" onBack={goBack} />
        <div className={styles.empty}>
          <Text size={12.5} color={colors.textMuted} align="center">
            ບໍ່ພົບການຮັບເຄື່ອງນີ້ໃນປະຫວັດ
          </Text>
        </div>
      </>
    );
  }

  return (
    <>
      <AppBar
        title={purchase.supplier}
        onBack={goBack}
        subtitle={
          <Text size={11} color={colors.onDarkSoft}>
            {purchase.paid ? (purchase.cash ? 'ຈ່າຍເງິນສົດ' : 'ຈ່າຍໂອນ') : 'ຄ້າງຈ່າຍ'} ·{' '}
            {fmtDate(new Date(purchase.at))}
          </Text>
        }
      />

      <div className={`${styles.body} vscroll`}>
        <Card elevated className={styles.sheet}>
          {purchase.lines.map((line, i) => {
            // The base unit comes off the product so the conversion can be
            // shown: a delivery taken in ໂຕນ lands on the shelf in ຖົງ, and
            // that is the figure a shopkeeper counts against. Retired products
            // are looked up too — retiring one does not unmake the deliveries
            // that brought it in.
            const product = [...catalog, ...retired].find((p) => p.id === line.productId);
            const base = product?.units[0]?.unit;
            return (
              <div key={`${line.productId}-${line.unit}`}>
                {i > 0 ? <div className={styles.dash} /> : null}
                <div className={styles.line}>
                  <div className="grow col">
                    <Text size={12.5} lines={2} align="left">
                      {line.name}
                    </Text>
                    <Mono size={10.5} color={colors.textFaint}>
                      {fmtQty(line.qty)} {line.unit}
                      {/* `unitCost` on a purchase is per the unit received, not
                          per base unit as it is on a sale — so it is labelled
                          with that unit rather than a bare "ຕໍ່ໜ່ວຍ". */}
                      {line.unitCost === undefined
                        ? ''
                        : ` × ${fmt(line.unitCost)} ₭/${line.unit}`}
                    </Mono>
                    {base && base !== line.unit ? (
                      <Mono size={10.5} color={colors.textFaint}>
                        = {fmtQty(line.qtyBase)} {base} ເຂົ້າສະຕັອກ
                      </Mono>
                    ) : null}
                  </div>
                  <Mono size={12.5} className="selectable">
                    {fmt(line.amount)}
                  </Mono>
                </div>
              </div>
            );
          })}

          <div className={styles.total}>
            <Text weight={800} size={14}>
              ລວມ
            </Text>
            <Mono weight={700} size={17} className="selectable">
              {fmt(purchase.total)} ₭
            </Mono>
          </div>
        </Card>

        {purchase.paid ? null : (
          <Card className={styles.note}>
            <Text weight={700} size={12.5} color={colors.skyDeep}>
              ຍັງບໍ່ໄດ້ຈ່າຍ
            </Text>
            {/* Said here as plainly as ຮັບເຄື່ອງເຂົ້າຮ້ານ says it: the goods and
                the cost are recorded, the money is not, and the app keeps no
                supplier balance to chase it with. */}
            <Text size={11} color={colors.textMuted}>
              ເຄື່ອງ ແລະ ຕົ້ນທຶນເຂົ້າແລ້ວ ແຕ່ເງິນຍັງບໍ່ອອກຈາກລິ້ນຊັກ · ແອັບຍັງບໍ່ຕິດຕາມໜີ້ຜູ້ຂາຍ
            </Text>
          </Card>
        )}

        <Text size={10.5} color={colors.textFaint} align="center">
          ຕົ້ນທຶນຄິດແບບສະເລ່ຍຖ່ວງນ້ຳໜັກ · ບິນທີ່ຂາຍໄປແລ້ວບໍ່ປ່ຽນ
        </Text>
      </div>
    </>
  );
}
